import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Tooltip,
  Snackbar,
  Switch,
  FormControlLabel,
  Skeleton,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  AccountBalanceWallet as WalletIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  History as HistoryIcon,
  SmartToy as BotIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  RestartAlt as ResetIcon,
  Analytics as AnalyticsIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  Psychology as StrategyIcon,
} from '@mui/icons-material';
import { MARKET_IDS as MARKETS } from '../constants/markets';
import PaperTradeLog from '../components/paper/PaperTradeLog';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

// --- Auth helpers ---
const getToken = () => {
  const tokensJson = localStorage.getItem('authTokens');
  if (tokensJson) {
    try { return JSON.parse(tokensJson).access_token; } catch { /* ignore */ }
  }
  return null;
};

const authHeaders = () => ({
  'Authorization': `Bearer ${getToken()}`,
  'Content-Type': 'application/json',
});

// --- Error helper ---
const formatApiError = (err: any): string => {
  if (!err) return 'Unbekannter Fehler';
  if (typeof err === 'string') return err;
  if (err.detail) {
    if (typeof err.detail === 'string') return err.detail;
    if (Array.isArray(err.detail)) {
      return err.detail
        .map((e: any) => {
          const loc = e.loc ? e.loc.join(' -> ') : '';
          return loc ? `${loc}: ${e.msg}` : e.msg;
        })
        .join('; ');
    }
  }
  if (err.message) return err.message;
  return JSON.stringify(err);
};

const apiFetch = async (path: string, options?: RequestInit) => {
  const { apiFetch: centralFetch } = await import('../utils/apiFetch');
  const res = await centralFetch(path, {
    ...options,
    headers: { ...authHeaders(), ...(options?.headers || {}) },
  });
  const data = await res.json();
  if (!res.ok) throw data;
  return data;
};

// --- Types ---
interface Portfolio {
  total_eur: number;
  initial_eur: number;
  pnl_eur: number;
  pnl_percent: number;
  positions: Position[];
}

interface Position {
  currency: string;
  amount: number;
  price: number;
  value_eur: number;
}

interface PaperTrade {
  id: number;
  created_at: string;
  market: string;
  side: string;
  amount: number;
  price: number;
  total: number;
  fee: number;
  strategy_id?: number;
  strategy_name?: string;
}

interface UserStrategy {
  id: number;
  name: string;
  strategy_type: string;
  config: Record<string, any>;
  is_active: boolean;
  total_trades?: number;
  winning_trades?: number;
  total_profit_loss?: number;
  last_executed?: string;
  description?: string;
}

interface RunnerStatus {
  running: boolean;
  interval_seconds?: number;
  active_strategies?: number;
  last_run?: string;
}

// --- TabPanel ---
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`paper-tabpanel-${index}`}
      aria-labelledby={`paper-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

// Deutsche Übersetzungen für alle Parameter
// Strategie-Icons und Farben
const STRATEGY_LABELS: Record<string, { icon: string; color: string; name: string }> = {
  marktmeister: { icon: '🧙', color: '#7C4DFF', name: 'MarktMeister' },
};


// ===========================
// Main Component
// ===========================
const PaperTradingPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Dashboard state
  const [portfolio, setPortfolio] = useState<Portfolio | null>(null);
  const [orderMarket, setOrderMarket] = useState('BTC-EUR');
  const [orderSide, setOrderSide] = useState<'buy' | 'sell'>('buy');
  const [orderAmount, setOrderAmount] = useState('');
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetBalance, setResetBalance] = useState('10000');
  const [confirmStopRunnerOpen, setConfirmStopRunnerOpen] = useState(false);

  // Strategies state (loaded for overview display)
  const [userStrategies, setUserStrategies] = useState<UserStrategy[]>([]);

  // Runner state
  const [runnerStatus, setRunnerStatus] = useState<RunnerStatus | null>(null);
  const [runnerInterval, setRunnerInterval] = useState('60');

  // Trade history state
  const [trades, setTrades] = useState<PaperTrade[]>([]);

  // --- Data loading ---
  const loadPortfolio = useCallback(async () => {
    try {
      const data = await apiFetch('/paper/portfolio');
      setPortfolio(data);
    } catch (err) {
      console.error('Failed to load portfolio:', err);
    }
  }, []);

  const loadTrades = useCallback(async () => {
    try {
      const data = await apiFetch('/paper/trades');
      setTrades(Array.isArray(data) ? data : data.trades || []);
    } catch (err) {
      console.error('Failed to load trades:', err);
    }
  }, []);

  const loadUserStrategies = useCallback(async () => {
    try {
      const data = await apiFetch('/strategies/');
      const list: UserStrategy[] = Array.isArray(data) ? data : data.strategies || [];
      // Dedupliziere nach ID (falls doppelt geladen)
      const seen = new Set<number>();
      const unique = list.filter(s => {
        if (seen.has(s.id)) return false;
        seen.add(s.id);
        return true;
      });
      setUserStrategies(unique);
    } catch (err) {
      console.error('Failed to load user strategies:', err);
    }
  }, []);

  const loadRunnerStatus = useCallback(async () => {
    try {
      const data = await apiFetch('/paper/runner/status');
      setRunnerStatus(data);
    } catch (err) {
      console.error('Failed to load runner status:', err);
    }
  }, []);

  const loadAllData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      await Promise.all([
        loadPortfolio(),
        loadTrades(),
        loadUserStrategies(),
        loadRunnerStatus(),
      ]);
    } catch (err) {
      setError('Fehler beim Laden der Paper-Trading-Daten');
    } finally {
      setIsLoading(false);
    }
  }, [loadPortfolio, loadTrades, loadUserStrategies, loadRunnerStatus]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // --- Handlers ---
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  // -- Order --
  const handlePlaceOrder = async () => {
    if (!orderAmount || parseFloat(orderAmount) <= 0) {
      showSnackbar('Bitte einen gültigen EUR-Betrag eingeben', 'error');
      return;
    }
    setIsSubmittingOrder(true);
    try {
      await apiFetch('/paper/order', {
        method: 'POST',
        body: JSON.stringify({
          market: orderMarket,
          side: orderSide,
          amount_eur: parseFloat(orderAmount),
        }),
      });
      showSnackbar(`${orderSide === 'buy' ? 'Kauf' : 'Verkauf'}-Order erfolgreich ausgeführt`, 'success');
      setOrderAmount('');
      await Promise.all([loadPortfolio(), loadTrades()]);
    } catch (err) {
      showSnackbar(`Fehler: ${formatApiError(err)}`, 'error');
    } finally {
      setIsSubmittingOrder(false);
    }
  };

  // -- Reset --
  const handleReset = async () => {
    try {
      await apiFetch('/paper/reset', {
        method: 'POST',
        body: JSON.stringify({ initial_balance_eur: parseFloat(resetBalance) }),
      });
      setResetDialogOpen(false);
      showSnackbar('Paper-Konto vollständig zurückgesetzt — alle Trades, Positionen und Strategien gelöscht', 'success');
      await loadAllData();
    } catch (err) {
      showSnackbar(`Fehler: ${formatApiError(err)}`, 'error');
    }
  };

  const handleToggleStrategy = async (id: number | string, currentlyActive: boolean) => {
    try {
      const endpoint = currentlyActive ? `/strategies/${id}/deactivate` : `/strategies/${id}/activate`;
      await apiFetch(endpoint, { method: 'POST' });
      showSnackbar(
        currentlyActive ? 'Strategie deaktiviert' : 'Strategie aktiviert',
        'success',
      );
      await loadUserStrategies();
    } catch (err) {
      showSnackbar(`Fehler: ${formatApiError(err)}`, 'error');
    }
  };

  // -- Runner --
  const handleStartRunner = async () => {
    try {
      await apiFetch('/paper/runner/start', {
        method: 'POST',
        body: JSON.stringify({ interval_seconds: parseInt(runnerInterval, 10) }),
      });
      showSnackbar('Auto-Trader gestartet', 'success');
      await loadRunnerStatus();
    } catch (err) {
      showSnackbar(`Fehler: ${formatApiError(err)}`, 'error');
    }
  };

  const handleStopRunner = async () => {
    try {
      await apiFetch('/paper/runner/stop', { method: 'POST' });
      showSnackbar('Auto-Trader gestoppt', 'success');
      await loadRunnerStatus();
    } catch (err) {
      showSnackbar(`Fehler: ${formatApiError(err)}`, 'error');
    }
  };

  const handleEmergencyStop = async () => {
    if (!window.confirm('NOTFALL-STOPP: Alle Strategien werden sofort deaktiviert (Paper + Live). Fortfahren?')) return;
    try {
      // apiFetch (oben definiert) liefert bereits das geparste JSON zurueck —
      // ein zweiter .json()-Aufruf darauf wuerde einen TypeError werfen
      // und den Notfall-Stopp faelschlich als Fehler darstellen (Audit Bug 2).
      const data = await apiFetch('/paper/runner/emergency-stop', { method: 'POST' });
      showSnackbar(`Notfall-Stopp: ${data.strategies_deactivated} Strategien deaktiviert`, 'success');
      await loadRunnerStatus();
      await loadAllData();
    } catch (err) {
      showSnackbar(`Fehler: ${formatApiError(err)}`, 'error');
    }
  };

  // --- Format helpers ---
  const fmtEur = (val: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(val);

  const fmtNum = (val: number, decimals = 6) =>
    new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: decimals }).format(val);

  const fmtDate = (ts: string) => {
    try {
      return new Intl.DateTimeFormat('de-DE', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(ts));
    } catch {
      return ts;
    }
  };

  // --- Render ---
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Paper Trading - BTrader</title>
      </Helmet>
      <Box sx={{ flexGrow: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h4" component="h1">
            Paper Trading
          </Typography>
          <Tooltip title="Daten aktualisieren">
            <IconButton onClick={loadAllData}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Paper sx={{ width: '100%', mb: 3 }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            aria-label="paper trading tabs"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab
              icon={<WalletIcon />}
              label="Dashboard"
              id="paper-tab-0"
              aria-controls="paper-tabpanel-0"
            />
            <Tab
              icon={<StrategyIcon />}
              label="Strategien"
              id="paper-tab-1"
              aria-controls="paper-tabpanel-1"
            />
            <Tab
              icon={<BotIcon />}
              label="Auto-Trader"
              id="paper-tab-2"
              aria-controls="paper-tabpanel-2"
            />
            <Tab
              icon={<HistoryIcon />}
              label="Trade-Log"
              id="paper-tab-3"
              aria-controls="paper-tabpanel-3"
            />
          </Tabs>

          <Box sx={{ p: 2 }}>
            {/* ====================== TAB 0: DASHBOARD ====================== */}
            <TabPanel value={currentTab} index={0}>
              <Grid container spacing={3}>
                {/* Portfolio Overview */}
                <Grid item xs={12}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderLeft: 4,
                      borderLeftColor: portfolio && portfolio.pnl_eur >= 0 ? 'success.main' : 'error.main',
                    }}
                  >
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Portfolio-Übersicht
                      </Typography>
                      {portfolio ? (
                        <Grid container spacing={3}>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="body2" color="text.secondary">
                              Gesamtwert
                            </Typography>
                            <Typography variant="h5" fontWeight={700}>
                              {fmtEur(portfolio.total_eur)}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="body2" color="text.secondary">
                              Startkapital
                            </Typography>
                            <Typography variant="h5" fontWeight={600}>
                              {fmtEur(portfolio.initial_eur)}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="body2" color="text.secondary">
                              Gewinn / Verlust
                            </Typography>
                            <Typography
                              variant="h5"
                              fontWeight={700}
                              color={portfolio.pnl_eur >= 0 ? 'success.main' : 'error.main'}
                              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                            >
                              {portfolio.pnl_eur >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                              {fmtEur(portfolio.pnl_eur)}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="body2" color="text.secondary">
                              Rendite
                            </Typography>
                            <Typography
                              variant="h5"
                              fontWeight={700}
                              color={portfolio.pnl_percent >= 0 ? 'success.main' : 'error.main'}
                            >
                              {portfolio.pnl_percent >= 0 ? '+' : ''}
                              {fmtNum(portfolio.pnl_percent, 2)}%
                            </Typography>
                          </Grid>
                        </Grid>
                      ) : (
                        <Skeleton variant="rectangular" height={80} />
                      )}
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
                      <Button
                        variant="outlined"
                        color="warning"
                        startIcon={<ResetIcon />}
                        onClick={() => setResetDialogOpen(true)}
                      >
                        Konto zurücksetzen
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>

                {/* Positions Table */}
                <Grid item xs={12} md={7}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Positionen
                    </Typography>
                    {portfolio && portfolio.positions && portfolio.positions.length > 0 ? (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Währung</TableCell>
                              <TableCell align="right">Menge</TableCell>
                              <TableCell align="right">Preis</TableCell>
                              <TableCell align="right">EUR-Wert</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {portfolio.positions.map((pos) => (
                              <TableRow key={pos.currency}>
                                <TableCell>
                                  <Chip label={pos.currency} size="small" variant="outlined" />
                                </TableCell>
                                <TableCell align="right">{fmtNum(pos.amount)}</TableCell>
                                <TableCell align="right">{fmtEur(pos.price)}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>
                                  {fmtEur(pos.value_eur)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Alert severity="info">Keine offenen Positionen vorhanden.</Alert>
                    )}
                  </Paper>
                </Grid>

                {/* Quick Order */}
                <Grid item xs={12} md={5}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Schnell-Order
                    </Typography>
                    <Box display="flex" flexDirection="column" gap={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Markt</InputLabel>
                        <Select
                          value={orderMarket}
                          label="Markt"
                          onChange={(e) => setOrderMarket(e.target.value)}
                        >
                          {MARKETS.map((m) => (
                            <MenuItem key={m} value={m}>
                              {m}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <ToggleButtonGroup
                        value={orderSide}
                        exclusive
                        onChange={(_e, val) => val && setOrderSide(val)}
                        fullWidth
                        size="small"
                      >
                        <ToggleButton
                          value="buy"
                          sx={{
                            '&.Mui-selected': {
                              backgroundColor: 'success.main',
                              color: 'white',
                              '&:hover': { backgroundColor: 'success.dark' },
                            },
                          }}
                        >
                          Kaufen
                        </ToggleButton>
                        <ToggleButton
                          value="sell"
                          sx={{
                            '&.Mui-selected': {
                              backgroundColor: 'error.main',
                              color: 'white',
                              '&:hover': { backgroundColor: 'error.dark' },
                            },
                          }}
                        >
                          Verkaufen
                        </ToggleButton>
                      </ToggleButtonGroup>

                      <TextField
                        label="Betrag (EUR)"
                        type="number"
                        size="small"
                        value={orderAmount}
                        onChange={(e) => setOrderAmount(e.target.value)}
                        inputProps={{ min: 0, step: 10 }}
                        fullWidth
                      />

                      <Button
                        variant="contained"
                        color={orderSide === 'buy' ? 'success' : 'error'}
                        startIcon={<SendIcon />}
                        onClick={handlePlaceOrder}
                        disabled={isSubmittingOrder || !orderAmount}
                        fullWidth
                      >
                        {isSubmittingOrder ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          `${orderSide === 'buy' ? 'Kaufen' : 'Verkaufen'} - ${orderAmount ? fmtEur(parseFloat(orderAmount)) : '0,00 €'}`
                        )}
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>

              {/* Reset Dialog */}
              <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
                <DialogTitle>Bist du sicher?</DialogTitle>
                <DialogContent>
                  <DialogContentText sx={{ mb: 2 }}>
                    Möchtest du dein Paper-Trading-Konto wirklich komplett zurücksetzen?

                    Folgendes wird unwiderruflich gelöscht:
                    • Alle Trades und die gesamte Trade-Historie
                    • Alle Krypto-Positionen (Balances)
                    • Alle Strategien und deren Einstellungen
                    • Der Strategy-Runner wird gestoppt

                    Das Konto startet danach komplett neu mit dem gewählten Startkapital.
                  </DialogContentText>
                  <TextField
                    label="Startkapital (EUR)"
                    type="number"
                    fullWidth
                    value={resetBalance}
                    onChange={(e) => setResetBalance(e.target.value)}
                    inputProps={{ min: 100, step: 1000 }}
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setResetDialogOpen(false)}>Abbrechen</Button>
                  <Button onClick={handleReset} color="warning" variant="contained">
                    Zurücksetzen
                  </Button>
                </DialogActions>
              </Dialog>
            </TabPanel>

            {/* ====================== TAB 1: STRATEGIEN (Verweis auf zentrale Seite) ====================== */}
            <TabPanel value={currentTab} index={1}>
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <StrategyIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  Strategien-Verwaltung
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}>
                  Erstelle, bearbeite und analysiere deine Trading-Strategien an einem zentralen Ort.
                  Die gleichen Strategien werden sowohl im Paper-Trading als auch im Live-Trading verwendet.
                </Typography>

                {/* Kurzübersicht aktiver Strategien */}
                {userStrategies.length === 0 && (
                  <Alert severity="info" sx={{ mb: 3, textAlign: 'left', maxWidth: 500, mx: 'auto' }}>
                    Du hast noch keine Strategien. Erstelle deine erste Strategie, um mit dem Paper Trading zu beginnen.
                  </Alert>
                )}
                {userStrategies.filter(s => s.is_active).length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Aktive Strategien
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                      {userStrategies.filter(s => s.is_active).map(s => {
                        const info = STRATEGY_LABELS[s.strategy_type] || { icon: '📊', color: 'text.secondary', name: s.strategy_type };
                        return (
                          <Chip
                            key={s.id}
                            icon={<Typography fontSize="0.9rem">{info.icon}</Typography>}
                            label={`${s.name} (${s.config?.market || '?'})`}
                            color="success"
                            variant="outlined"
                          />
                        );
                      })}
                    </Box>
                  </Box>
                )}

                <Button
                  variant="contained"
                  size="large"
                  startIcon={<StrategyIcon />}
                  onClick={() => navigate('/strategies')}
                  sx={{ px: 4, py: 1.5 }}
                >
                  Zur Strategien-Verwaltung
                </Button>
              </Box>
            </TabPanel>

            {/* ====================== TAB 2: AUTO-TRADER ====================== */}
            <TabPanel value={currentTab} index={2}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Strategy Runner
                      </Typography>

                      {/* Status */}
                      <Box display="flex" alignItems="center" gap={2} mb={3}>
                        <Typography variant="body1">Status:</Typography>
                        <Chip
                          label={runnerStatus?.running ? 'Läuft' : 'Gestoppt'}
                          color={runnerStatus?.running ? 'success' : 'default'}
                          icon={runnerStatus?.running ? <PlayIcon /> : <StopIcon />}
                        />
                      </Box>

                      {runnerStatus && (
                        <Box mb={3}>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">
                                Intervall
                              </Typography>
                              <Typography variant="body1" fontWeight={600}>
                                {runnerStatus.interval_seconds}s
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">
                                Aktive Strategien
                              </Typography>
                              <Typography variant="body1" fontWeight={600}>
                                {runnerStatus.active_strategies}
                              </Typography>
                            </Grid>
                            {runnerStatus.last_run && (
                              <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">
                                  Letzter Lauf
                                </Typography>
                                <Typography variant="body1">
                                  {fmtDate(runnerStatus.last_run)}
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      )}

                      {/* Interval setting */}
                      <TextField
                        label="Intervall (Sekunden)"
                        type="number"
                        size="small"
                        value={runnerInterval}
                        onChange={(e) => setRunnerInterval(e.target.value)}
                        inputProps={{ min: 10, step: 10 }}
                        fullWidth
                        sx={{ mb: 2 }}
                        disabled={runnerStatus?.running}
                      />

                      {/* Controls */}
                      <Box display="flex" gap={2}>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<PlayIcon />}
                          onClick={handleStartRunner}
                          disabled={runnerStatus?.running}
                          fullWidth
                        >
                          Starten
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          startIcon={<StopIcon />}
                          onClick={() => setConfirmStopRunnerOpen(true)}
                          disabled={!runnerStatus?.running}
                          fullWidth
                        >
                          Stoppen
                        </Button>
                      </Box>

                      <Dialog open={confirmStopRunnerOpen} onClose={() => setConfirmStopRunnerOpen(false)}>
                        <DialogTitle>Bist du sicher?</DialogTitle>
                        <DialogContent>
                          <DialogContentText>
                            Möchtest du den Strategy-Runner wirklich stoppen? Aktive Strategien werden pausiert.
                          </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                          <Button onClick={() => setConfirmStopRunnerOpen(false)}>Abbrechen</Button>
                          <Button onClick={() => { setConfirmStopRunnerOpen(false); handleStopRunner(); }} color="error" variant="contained">
                            Ja, stoppen
                          </Button>
                        </DialogActions>
                      </Dialog>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Notfall-Stopp */}
                <Grid item xs={12}>
                  <Alert
                    severity="error"
                    variant="outlined"
                    action={
                      <Button color="error" size="small" variant="contained" onClick={handleEmergencyStop}>
                        Notfall-Stopp
                      </Button>
                    }
                    sx={{ borderRadius: 2 }}
                  >
                    <strong>Notfall-Stopp</strong> — Stoppt den Runner und deaktiviert sofort alle Strategien (Paper + Live).
                  </Alert>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Hinweise
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Der Auto-Trader führt alle aktiven Strategien automatisch im eingestellten
                      Intervall aus. Stellen Sie sicher, dass mindestens eine Strategie aktiv ist,
                      bevor Sie den Runner starten.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Aktive Strategien können im Tab &quot;Strategien&quot; verwaltet werden.
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </TabPanel>

            {/* ====================== TAB 3: TRADE-LOG ====================== */}
            <TabPanel value={currentTab} index={3}>
              <PaperTradeLog />
            </TabPanel>
          </Box>
        </Paper>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PaperTradingPage;
